import { useCallback, useEffect, useState } from 'react';
import { GetAdminCompaniesAPI } from '../../../../api/AdminAPI';

const DEFAULT_SUMMARY = { total: 0, active: 0, inactive: 0, never_accessed: 0, test: 0, grace: 0, expired: 0 };
const DEFAULT_PAGINATION = { total: 0, page: 1, limit: 25, pages: 1 };
const SEARCH_DEBOUNCE_MS = 400;

/**
 * useAdminCompanies
 *
 * Hook responsável pelo estado de filtros, ordenação e paginação da listagem
 * de empresas do Admin Geral, além de buscar os dados via AdminAPI.
 *
 * @returns {{
 *   companies: Array,
 *   summary: Object,
 *   pagination: Object,
 *   isLoading: boolean,
 *   error: string|null,
 *   searchInput: string,
 *   setSearchInput: Function,
 *   status: string,
 *   setStatus: Function,
 *   isTest: string,
 *   setIsTest: Function,
 *   closure: string,
 *   setClosure: Function,
 *   sortValue: string,
 *   setSortValue: Function,
 *   page: number,
 *   setPage: Function,
 *   limit: number,
 *   setLimit: Function,
 *   refresh: Function,
 * }}
 */
function useAdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatusState] = useState('all');
  const [isTest, setIsTestState] = useState('all');
  // Encerramento da conta: all | grace | expired | none
  const [closure, setClosureState] = useState('all');
  // sortValue combina sort + order em uma única opção de UI (ex: "created_at_desc")
  const [sortValue, setSortValueState] = useState('created_at_desc');

  // Paginação
  const [page, setPageState] = useState(1);
  const [limit, setLimitState] = useState(25);

  // Debounce da busca por nome
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPageState(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const setStatus = useCallback((value) => {
    setStatusState(value);
    setPageState(1);
  }, []);

  const setIsTest = useCallback((value) => {
    setIsTestState(value);
    setPageState(1);
  }, []);

  const setClosure = useCallback((value) => {
    setClosureState(value);
    setPageState(1);
  }, []);

  const setSortValue = useCallback((value) => {
    setSortValueState(value);
    setPageState(1);
  }, []);

  const setLimit = useCallback((value) => {
    setLimitState(value);
    setPageState(1);
  }, []);

  const setPage = useCallback((value) => {
    setPageState(value);
  }, []);

  const [sort, order] = (() => {
    // "created_at_desc" -> ["created_at", "desc"]; "last_login_at_desc" -> ["last_login_at", "desc"]
    const idx = sortValue.lastIndexOf('_');
    return [sortValue.slice(0, idx), sortValue.slice(idx + 1)];
  })();

  const fetchCompanies = useCallback(() => {
    setIsLoading(true);
    setError(null);
    GetAdminCompaniesAPI(
      { search, status, is_test: isTest, closure, sort, order, page, limit },
      (response) => {
        if (!response || response.success === false) {
          setError(response?.error || 'Erro ao carregar empresas. Tente novamente.');
          setIsLoading(false);
          return;
        }
        setCompanies(response.companies || []);
        setSummary(response.summary || DEFAULT_SUMMARY);
        setPagination(response.pagination || { ...DEFAULT_PAGINATION, page, limit });
        setIsLoading(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, isTest, closure, sort, order, page, limit]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  return {
    companies,
    summary,
    pagination,
    isLoading,
    error,
    searchInput,
    setSearchInput,
    status,
    setStatus,
    isTest,
    setIsTest,
    closure,
    setClosure,
    sortValue,
    setSortValue,
    page,
    setPage,
    limit,
    setLimit,
    refresh: fetchCompanies,
  };
}

export default useAdminCompanies;
